import { FC, useState } from 'react'
import { useNavigate } from 'react-router'
import { BetTypeEnum, LottoBetList } from '../BetLotto.types'
import { FiChevronUp, FiChevronDown } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import BetControlItem from './BetItem'
import {
  CurrencyObject,
  formatBetPriceWithCurrencyObject,
  getCurrencyDisplayFromObject,
} from '../../../utils/currency-utils'
import { DEFAULT_CURRENCY } from '../../../utils/currency-config'
import { USER_BALANCE_UNDEFINED } from '../../../constants/string'
import { useThemeFeatures } from '../../../hooks/useThemeFeatures'
import styles from './BetControls.module.css'

export interface BetControlsProps {
  betData: LottoBetList[]
  onChangeSingleBetPrice: (
    type: BetTypeEnum,
    number: string,
    amount: number,
  ) => void
  onDeleteItem: (type: BetTypeEnum, number: string, idx: number) => void
  onClearAll: () => void
  onBetSubmit: () => void
  totalBetAmount: number
  creditLeft?: number
  onChangeBetPrice: (amount: number) => void
  pollListUrl?: string
  betSetNumber?: number[]
  currencyObject?: CurrencyObject
  showWinAmount?: boolean
}

export const BetControls: FC<BetControlsProps> = ({
  betData,
  onChangeSingleBetPrice,
  onDeleteItem,
  onClearAll,
  onBetSubmit,
  totalBetAmount,
  creditLeft,
  onChangeBetPrice,
  pollListUrl,
  currencyObject,
  betSetNumber,
  showWinAmount = true,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const themeFeatures = useThemeFeatures()
  const [inputNumber, setInputNumber] = useState('')
  const currencySymbol = getCurrencyDisplayFromObject(currencyObject)

  const handleCustomAmount = (value: string) => {
    const amount = parseInt(value, 10)
    if (!isNaN(amount) && amount >= 0) {
      onChangeBetPrice(amount)
    }
    setInputNumber(value)
  }

  const handleQuickAmount = (amount: number) => {
    onChangeBetPrice(amount)
  }

  const renderItem = betData?.filter((bet) => bet.items.length > 0)
  const isEmpty = renderItem?.length === 0

  return (
    <div
      className={`flex flex-col h-auto rounded-lg bg-[var(--panel-bg)] border border-[var(--panel-border)] ${styles['bet-controls']}`}
    >
      <div className="p-4 md:p-6">
        <div className="flex justify-between items-center pb-4">
          <h5 className="text-[var(--caption-text)] text-base font-medium m-0">
            {t('betDetailPanel.listBet')}
          </h5>
          <button
            type="button"
            onClick={() => navigate(pollListUrl ?? '/number-set')}
            className={`px-4 py-3 rounded-lg font-semibold text-base cursor-pointer ${styles['bet-list-button']}`}
          >
            {t('betDetailPanel.listNumber')}
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {/* Scrollable bet list with fixed max height */}
          <div className="flex flex-col gap-4 max-h-[825px] overflow-y-auto overflow-x-hidden bet-list-scrollbar">
            {renderItem?.map((bet) => {
              return (
                <div key={bet.name} className="flex flex-col gap-2 pr-2">
                  <h6 className="text-[var(--betcontrols-heading)] font-medium sticky top-0 bg-[var(--panel-bg)] py-2 z-10">
                    {t(bet.name)}
                  </h6>
                  <div className="space-y-2">
                    {bet?.items?.map((item, idx) => {
                      return (
                        <BetControlItem
                          key={`${item.number}-${bet.name}`}
                          idx={idx}
                          type={bet.type}
                          number={item.number}
                          price={item.price}
                          rate={item.rate}
                          winPrice={item.winPrice}
                          onDeleteItem={onDeleteItem}
                          onChangeSingleBetPrice={onChangeSingleBetPrice}
                          currencyObject={currencyObject || DEFAULT_CURRENCY}
                          showWinAmount={showWinAmount}
                        />
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Fixed bottom section with unified input and amount controls */}
      {!isEmpty ? (
        <div className="p-4 md:p-6">
          {/* Divider above input */}
          <div className="border-t border-[var(--panel-border)] mb-4"></div>

          {/* Input number */}
          <div className="mb-4">
            <div className="relative">
              {themeFeatures.showCurrencyIconInInput && (
                <>
                  <div className="absolute left-6 top-1/2 -translate-y-1/2 text-[var(--bet-type-text)] font-semibold text-base pointer-events-none z-10">
                    {currencySymbol}
                  </div>
                  <div className="absolute left-12 top-0 bottom-0 w-px bg-[var(--bet-type-input-border)] pointer-events-none"></div>
                </>
              )}
              <input
                type="number"
                className={`w-full py-3 rounded-lg bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] font-semibold text-base outline-none border border-[var(--bet-type-border)] hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)] focus:bg-[var(--bet-type-hover-bg)] focus:text-[var(--bet-type-hover-text)] focus:border-[var(--bet-type-active-border)] appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                  themeFeatures.showCurrencyIconInInput ? 'pl-15 pr-4 ' : 'px-4'
                }`}
                placeholder={t('betDetailPanel.enterCustomAmount')}
                value={inputNumber}
                onChange={(e) => handleCustomAmount(e.target.value)}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col">
                <button
                  type="button"
                  aria-label={t('betDetailPanel.increaseAmount')}
                  className="flex items-center justify-center text-[var(--bet-type-text)] hover:text-[var(--bet-type-hover-text)] cursor-pointer transition-colors"
                  onClick={() => {
                    const currentValue = parseInt(inputNumber) || 0
                    handleCustomAmount((currentValue + 1).toString())
                  }}
                >
                  <FiChevronUp size={12} />
                </button>
                <button
                  type="button"
                  aria-label={t('betDetailPanel.decreaseAmount')}
                  className="flex items-center justify-center text-[var(--bet-type-text)] hover:text-[var(--bet-type-hover-text)] cursor-pointer -mt-1 transition-colors"
                  onClick={() => {
                    const currentValue = parseInt(inputNumber) || 0
                    if (currentValue > 0) {
                      handleCustomAmount((currentValue - 1).toString())
                    }
                  }}
                >
                  <FiChevronDown size={12} />
                </button>
              </div>
            </div>
          </div>

          {/* Quick amount buttons */}
          <div className="grid grid-cols-4 gap-3">
            {betSetNumber?.map((amount) => (
              <button
                key={amount}
                onClick={() => handleQuickAmount(amount)}
                className="h-[42px] flex items-center justify-center rounded-lg border text-sm transition-all cursor-pointer bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)]"
              >
                {formatBetPriceWithCurrencyObject(
                  amount,
                  currencyObject,
                  themeFeatures.hideCurrencySymbolInBetControls,
                )}
              </button>
            ))}
          </div>

          {/* Divider below quick amount buttons */}
          <div className="border-t border-[var(--panel-border)] mt-4"></div>
        </div>
      ) : (
        <div className="p-4 md:p-6 flex items-center justify-center">
          <span className="text-[var(--betcontrols-muted-text)] text-base">
            {t('betDetailPanel.emptyList')}
          </span>
        </div>
      )}

      {/* Summary and action buttons */}
      <div className="p-4 md:p-6">
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex justify-between items-center text-sm">
            <span className="text-[var(--betcontrols-muted-text)]">
              {t('betDetailPanel.creditAmount')}
            </span>
            <span className="text-xl font-bold text-[var(--betcontrols-heading)]">
              {creditLeft !== undefined
                ? formatBetPriceWithCurrencyObject(
                    creditLeft,
                    currencyObject,
                    false,
                    themeFeatures.spaceBetweenCurrencyAndNumberInBetControls,
                  )
                : USER_BALANCE_UNDEFINED}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-[var(--betcontrols-muted-text)]">
              {t('betDetailPanel.betAmount')}
            </span>
            <span className="text-xl font-bold text-[var(--betcontrols-heading)]">
              {formatBetPriceWithCurrencyObject(
                Number(totalBetAmount),
                currencyObject,
                false,
                themeFeatures.spaceBetweenCurrencyAndNumberInBetControls,
              )}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className={`py-3 px-4 rounded-lg font-semibold text-base transition-all ${
              renderItem?.length === 0
                ? 'bg-[var(--betcontrols-disabled-bg)] text-[var(--betcontrols-disabled-text)] cursor-not-allowed'
                : 'bg-[var(--betcontrols-clear-bg)] clear-data-gradient border border-[var(--betcontrols-clear-border)] text-[var(--betcontrols-clear-text)] hover:bg-[var(--betcontrols-clear-hover-bg)] hover:text-[var(--betcontrols-clear-hover-text)] cursor-pointer'
            }`}
            onClick={onClearAll}
            disabled={renderItem?.length === 0}
          >
            {t('betDetailPanel.clear')}
          </button>
          <button
            type="button"
            className={`py-3 px-4 rounded-lg font-semibold text-base cursor-pointer transition-all disabled:bg-[var(--betcontrols-disabled-bg)] disabled:text-[var(--betcontrols-disabled-text)] disabled:cursor-not-allowed ${
              !(renderItem?.length === 0 || isEmpty)
                ? 'bet-button-active'
                : 'text-[var(--betcontrols-submit-text)]'
            }`}
            disabled={renderItem?.length === 0 || isEmpty}
            onClick={onBetSubmit}
          >
            {t('betDetailPanel.bet')}
          </button>
        </div>
      </div>
    </div>
  )
}
